import { useState } from 'react';
import round from '../../../helpers/round';
import { Alert, Typography, Box, IconButton, Collapse, Tooltip, useMediaQuery } from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Link from '@mui/material/Link';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleSuccessIcon from '@mui/icons-material/CheckCircleOutline';
import WaterCase1 from '../../../assets/calculations/hansen/WaterCase1.jpg';
import WaterCase2 from '../../../assets/calculations/hansen/WaterCase2.jpg';
import WaterCase3 from '../../../assets/calculations/hansen/WaterCase3.jpg';
import TransitionsModal from '../../modals/TransitionsModal';

const HansenResultEffectOfGroundWater = ({ result }) => {
  const isSmallScreen = useMediaQuery('(max-width:700px)');
  const { df, fndWidth, gwt, gamma } = result.input;
  const { canUserSeeCalculationSteps, phips, h, dw, waterLevelCase, gammaEffectiveInitial, gammaEffective, GRAVITY } =
    result.output;

  const case1 = waterLevelCase === 1;
  const case2 = waterLevelCase === 2;
  const case3 = waterLevelCase === 3;
  const caseLegendItems = [
    {
      label: 'CASE 1',
      description: 'Df < GWT \u2264 H (between base and wedge)',
      isActive: case1,
    },
    {
      label: 'CASE 2',
      description: 'GWT \u2264 Df (water above foundation base)',
      isActive: case2,
    },
    {
      label: 'CASE 3',
      description: 'GWT \u2265 H (below wedge depth)',
      isActive: case3,
    },
  ];
  const [showGammaEffectiveDetails, setShowGammaEffectiveDetails] = useState(false);

  const renderCaseBadge = (label, sx = {}) => (
    <Box
      component="span"
      sx={{
        ml: 1,
        px: 1,
        py: 0.2,
        borderRadius: 999,
        border: '1px solid',
        borderColor: 'success.light',
        color: 'success.dark',
        backgroundColor: 'rgba(46, 125, 50, 0.12)',
        fontSize: '0.72rem',
        fontWeight: 700,
        letterSpacing: 0.3,
        lineHeight: 1.2,
        display: 'inline-flex',
        alignItems: 'center',
        ...sx,
      }}
    >
      {label}
    </Box>
  );

  const successAlertSx = {
    backgroundColor: 'rgba(46,125,50,0.04)',
    alignItems: 'flex-start',
    '& .MuiAlert-icon': {
      alignSelf: 'flex-start',
      mt: 0.25,
      minWidth: 24,
    },
    '& .MuiAlert-action': {
      alignSelf: 'flex-start',
      mt: 0.25,
      width: 32,
      justifyContent: 'center',
      p: 0,
      ml: 1,
    },
    '& .MuiAlert-message': { width: '100%' },
  };

  const renderAlertAction = (isExpanded, setExpanded) =>
    canUserSeeCalculationSteps ? (
      <IconButton size="small" color="inherit" onClick={() => setExpanded((v) => !v)} sx={{ width: 32, height: 32 }}>
        <ExpandMoreIcon
          fontSize="small"
          sx={{
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.15s',
          }}
        />
      </IconButton>
    ) : (
      <Tooltip
        title={
          <span>
            Upgrade to unlock calculation details.{' '}
            <Link href="/pricing" target="_blank" rel="noopener" style={{ color: '#90caf9', textDecoration: 'underline' }}>
              See plans
            </Link>
          </span>
        }
        leaveDelay={500}
        arrow
      >
        <LockOutlinedIcon
          sx={{
            fontSize: 20,
            color: 'text.disabled',
            cursor: 'not-allowed',
            mr: 1,
            mt: 0.5,
          }}
        />
      </Tooltip>
    );

  const HCalculationDetails = () => (
    <>
      <Typography variant="body2" color="text.secondary" mb={1}>
        H = D<sub>f</sub> + 0.5 B tan(45 + &phi;/2)
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        H = {round(df, 2)} + (0.5) ({round(fndWidth, 2)}) [tan(45 + {round(phips, 2)} / 2)]
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        H = {round(h, 2)}m
      </Typography>
    </>
  );

  const WaterLevelCase1Details = () => (
    <>
      <Box mt={2}>
        <HCalculationDetails />
      </Box>
      <Typography variant="body2" color="text.secondary" mb={1}>
        &darr;
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        GWT = {round(gwt, 2)} &gt; D<sub>f</sub> = {round(df, 2)} and GWT = {round(gwt, 2)} &le; H = {round(h, 2)}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        &darr;
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        Ground water level is below foundation base and above wedge length of foundation.
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        In this case use &gamma;<sub>e</sub> as follows:
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        &darr;
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        &gamma;<sub>e</sub> = (2H - d<sub>w</sub>) (d<sub>w</sub> / H<sup>2</sup>) (&gamma;<sub>sat</sub>) + (&gamma;' / H
        <sup>2</sup>) (H - d<sub>w</sub>)<sup>2</sup>
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        Where;
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        H = Wedge zone measured from ground level.
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        d<sub>w</sub> = Depth to water table below base of footing.
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        &gamma;<sub>sat</sub> = Saturated unit weight of soil in depth d<sub>w</sub>
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        &gamma;' = Submerged unit weight of soil below water table
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        &darr;
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        d<sub>w</sub> = GWT - D<sub>f</sub>
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        d<sub>w</sub> = {round(gwt, 2)} - {round(df, 2)}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        d<sub>w</sub> = {round(dw, 2)}m
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        &darr;
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        &gamma;' = &gamma;<sub>sat</sub> - &gamma;<sub>w</sub>
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        &gamma;' = {round(gamma, 2)} - {round(GRAVITY, 2)}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        &gamma;' = {round(gammaEffectiveInitial, 2)}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        &darr;
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        &gamma;<sub>e</sub> = [2 ({round(h, 2)}) - {round(dw, 2)}] ({round(dw, 2)} / {round(h, 2)}
        <sup>2</sup>) ({round(gamma, 2)}) + ({round(gammaEffectiveInitial, 2)} / {round(h, 2)}
        <sup>2</sup>) ({round(h, 2)} - {round(dw, 2)})<sup>2</sup>
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        &gamma;<sub>e</sub> = {round(gammaEffective, 2)}m
      </Typography>
      <Box mt={1}>
        <TransitionsModal
          image={WaterCase1}
          title="Ground Water Level"
          description="Ground water level is below foundation base and above wedge length of foundation."
        />
      </Box>
    </>
  );

  const WaterLevelCase2Details = () => (
    <>
      <Typography variant="body2" color="text.secondary" mb={1} mt={2}>
        GWT = {round(gwt, 2)} &nbsp; &le; &nbsp; D<sub>f</sub> = {round(df, 2)}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        &darr;
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        Ground water level is above foundation base.
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        So, all the wedge length of the foundation is submerged into water.
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        &darr;
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        &gamma;<sub>e</sub> = &gamma;' = &gamma; - &gamma;<sub>w</sub>
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        &gamma;<sub>e</sub> = {round(gamma, 2)} - {round(GRAVITY, 2)}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        &gamma;<sub>e</sub> = {round(gammaEffective, 2)}
      </Typography>
      <Box mt={1}>
        <TransitionsModal
          image={WaterCase2}
          title="Ground Water Level"
          description="Ground water level is above foundation base. So, all the wedge length of the foundation is submerged into water."
        />
      </Box>
    </>
  );

  const WaterLevelCase3Details = () => (
    <>
      <Box mt={2}>
        <HCalculationDetails />
      </Box>
      <Typography variant="body2" color="text.secondary" mb={1}>
        &darr;
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        GWT = {round(gwt, 2)} &nbsp; &ge; &nbsp; H = {round(h, 2)}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        &darr;
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        Ground water level is below wedge length of the foundation.
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        So, ground water has no effect on bearing capacity of the soil for this foundation.
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        &darr;
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        &gamma;<sub>e</sub> = &gamma;
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        &gamma;<sub>e</sub> = {round(gamma, 2)}
      </Typography>
      <Box mt={1}>
        <TransitionsModal
          image={WaterCase3}
          title="Ground Water Level"
          description="Ground water level (GWT) is below wedge length of the foundation (Df + Hw). Therefore, ground water has no effect on bearing capacity of the soil for this foundation."
        />
      </Box>
    </>
  );

  const HCalculation = () => (
    <>
      <p>
        H = D<sub>f</sub> + 0.5 B tan(45 + &phi;/2)
      </p>
      <p>
        H = {round(df, 2)} + (0.5) ({round(fndWidth, 2)}) [tan(45 + {round(phips, 2)} / 2)]
      </p>
      <p>H = {round(h, 2)}m</p>
    </>
  );

  return (
    <Box sx={{ my: 2, textAlign: 'justify' }}>
      <Alert severity="info" variant="outlined" icon={isSmallScreen ? false : <InfoOutlinedIcon />}>
        <Typography variant="subtitle1" color="text.secondary" fontWeight={600} sx={{ mb: 1, fontSize: '1.05rem' }}>
          Effect of Ground Water
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6, textAlign: 'justify' }}>
          This section computes average effective unit weight of soil (&gamma;<sub>e</sub>) based on the groundwater position
          relative to the foundation base depth (D<sub>f</sub>) and wedge depth (H). Three water-level cases are evaluated:
          between foundation base and wedge depth, above the foundation base, or below the wedge depth. Each case applies a
          different &gamma;<sub>e</sub> formulation to capture the correct submerged or partially submerged soil condition used in
          the Hansen bearing-capacity terms.
        </Typography>
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            border: '1px solid',
            borderColor: 'info.light',
            borderRadius: 2,
          }}
        >
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" color="text.secondary" fontWeight={700}>
              Water-Level Cases
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr' }, gap: 0.8 }}>
            {caseLegendItems.map((item) => (
              <Box
                key={item.label}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  border: '1px solid',
                  borderColor: item.isActive ? 'success.main' : 'divider',
                  borderRadius: 1.5,
                  px: 1,
                  py: 0.7,
                  backgroundColor: item.isActive ? 'rgba(46,125,50,0.08)' : 'rgba(255,255,255,0.45)',
                }}
              >
                {renderCaseBadge(item.label, {
                  ml: 0,
                  minWidth: 64,
                  justifyContent: 'center',
                  borderColor: item.isActive ? 'success.main' : 'divider',
                  color: item.isActive ? 'success.dark' : 'text.disabled',
                  backgroundColor: item.isActive ? 'rgba(46, 125, 50, 0.16)' : 'rgba(0, 0, 0, 0.03)',
                  fontWeight: item.isActive ? 700 : 600,
                })}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: '0.82rem', fontWeight: item.isActive ? 600 : 500 }}
                >
                  {item.description}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Success Alert: Effective Unit Weight */}
          <Alert
            icon={isSmallScreen ? false : <CheckCircleSuccessIcon />}
            severity="success"
            variant="outlined"
            sx={successAlertSx}
            action={renderAlertAction(showGammaEffectiveDetails, setShowGammaEffectiveDetails)}
          >
              <Typography variant="body2" color="text.secondary" component="div">
                <b>
                  &gamma;<sub>e</sub> (Average effective unit weight of soil) = {round(gammaEffective, 2)} kN/m
                  <sup>3</sup>
                </b>
              </Typography>

            {canUserSeeCalculationSteps && (
              <Collapse in={showGammaEffectiveDetails} unmountOnExit>
                {case1 && <WaterLevelCase1Details />}
                {case2 && <WaterLevelCase2Details />}
                {case3 && <WaterLevelCase3Details />}
                {!case1 && !case2 && !case3 && (
                  <Typography variant="body2" color="text.secondary" mt={2}>
                    Water level case is not available.
                  </Typography>
                )}
              </Collapse>
            )}
          </Alert>
        </Box>
      </Alert>
    </Box>
  );
};
export default HansenResultEffectOfGroundWater;
